import { Col, Divider, Row } from 'antd';
import React from 'react';

import { Evidence, EvidenceSchema } from '../evidence/evidence';
import { Tag } from '../index';
import type { TagCategory } from '../index';
import styles from './timeline-slip.styles';

/**
 * Props para el componente TimelineSlip.
 */
export type TimelineSlipProps = {
  /**
   * Una etiqueta opcional para mostrar en el recibo.
   */
  tag?: { text: string; type?: TagCategory };
  /**
   * La fecha asociada con el pedido.
   */
  orderDate: string;
  /**
   * Una cadena que representa la cantidad de artículos.
   */
  quantity: string;
  /**
   * Una etiqueta descriptiva para el pedido.
   */
  orderLabel: string;
  /**
   * El número de pedido o un identificador único.
   */
  orderNumber: string;
  /**
   * Una URL opcional para vincular a más detalles, a menudo utilizada en la sección de evidencia.
   */
  hrefUrl?: string;
  /**
   * Un array de elementos de evidencia (como firmas o fotos) asociados con el recibo.
   */
  evidences?: Array<EvidenceSchema>;
};

/**
 * Un componente que renderiza un solo artículo o "recibo" dentro de un paso de la línea de tiempo.
 * Está diseñado para mostrar los detalles del pedido y la evidencia asociada.
 *
 * @param {TimelineSlipProps} props - Las props para el componente TimelineSlip.
 *
 * @returns {JSX.Element} El componente TimelineSlip renderizado.
 */
const TimelineSlip = ({
  tag,
  orderDate,
  quantity,
  orderLabel,
  orderNumber,
  hrefUrl,
  evidences
}: TimelineSlipProps) => {
  return (
    <Col style={styles.card}>
      <Row style={styles.cardRow}>
        <Col>
          <Row style={styles.cardLeft}>
            {tag && <Tag type={tag.type}>{tag.text}</Tag>}
            <span style={styles.cardDate}>{orderDate}</span>
          </Row>
        </Col>

        <Col>
          <span style={styles.cardCenter}>{quantity}</span>
        </Col>

        <Col>
          <Row style={styles.cardRight}>
            <span style={styles.cardLabel}>{orderLabel}</span>
            <span className='font-semibold' style={styles.cardNumber}>
              {orderNumber}
            </span>
          </Row>
        </Col>
      </Row>
      {evidences && evidences.length > 0 && (
        <>
          <Divider />
          <Evidence
            signName={evidences.find((e) => e.type === 'text')?.value}
            signUrl={evidences.find((e) => e.type === 'signature')?.value}
            imageEvidence={evidences.find((e) => e.type === 'photo')}
            hrefUrl={hrefUrl}
          />
        </>
      )}
    </Col>
  );
};

export default TimelineSlip;
